/**
 * Shared constants for the `/app/access/*` surfaces.
 *
 * Page sizes are CLIENT-side here, and that is a property of the API, not a shortcut: `QueryRolesDto` and
 * `QueryPoliciesDto` accept `skip`, `limit` and `organization_id` and nothing else, and the global
 * `ValidationPipe` runs `forbidNonWhitelisted`, so a `?search=` would answer 400. The catalogs are small and
 * bounded (86 seeded roles, 86 seeded policies) and both hooks already page them to exhaustion, so filtering
 * and paging in memory describes the WHOLE catalog rather than one server page — which is the honest option.
 * If either catalog ever outgrows that, the fix is a server-side filter, not a silent cap.
 */

/** Rows per page in the catalog tables. */
export const ACCESS_TABLE_PAGE_SIZE = 15;

/**
 * How many API keys the module asks for, in one request.
 *
 * 200 is the API's own ceiling (`PaginationDto` caps `limit` at `Max(200)`), not a number we chose. Settings
 * keeps its own smaller cap; this page raises it because a silent cap on the view whose entire subject is the
 * key catalog would under-report it — and its search and role filters, which run in the browser, would then
 * be filtering a window while looking like they filter everything.
 *
 * It is not raised further by looping pages, and that is deliberate: `GET /api-keys` has no `include=roles`,
 * so every key costs one extra request to resolve its roles. Beyond 200 the honest answer is the truncation
 * notice the page already renders, not a fan-out nobody asked for.
 */
export const ACCESS_KEYS_FETCH_LIMIT = 200;

/** The "no filter" sentinel. Never reaches the wire — these filters never leave the browser. */
export const ACCESS_FILTER_ALL = "all" as const;

/**
 * Tenancy filter, shared by the roles and policies catalogs: both entities carry the same `organization_id |
 * null` and the same "global rows are read-only for an organization" rule.
 */
export type AccessScopeFilter = typeof ACCESS_FILTER_ALL | "global" | "org";
